// ════════════════════════════════════════════════════════════════════════════
//  Secure Channel — Finding 18 fix + Network tab hiding (React)
//
//  • कोई static key नहीं। REACT_APP_ENC_KEY पूरी तरह हटाना है।
//  • हर tab/page-load पर नया ECDH P-256 key pair (private key non-extractable)
//  • Server के साथ AES-256-GCM session key बनती है जो सिर्फ memory में रहती है
//  • हर axios call → POST /api/v2/<random>  { c, n, d }  (असली URL, method, body, headers अंदर encrypted)
//    <random> हर call पर नया: a439hfjas93498, i-212394282934, 3f9a0c1b7e22 ...
//  • Response → हमेशा 200 { n, d }  (असली status, data अंदर encrypted)
//  • Outer request "simple request" है (text/plain, कोई custom header नहीं),
//    इसलिए random URL होने पर भी browser हर call से पहले OPTIONS preflight नहीं भेजता
//
//  Network tab में हर call का नाम random दिखेगा, कभी दोहराएगा नहीं।
//  axiosInstance (AxiosInstance.jsx) पर adapter लगा है; plain axios calls के लिए
//  index.js में installGlobalAxios() call होता है।
// ════════════════════════════════════════════════════════════════════════════

import axios, { AxiosError, AxiosHeaders, CanceledError } from "axios";
import { baseUrl, REACT_APP_SECURE_CHANNEL } from "../Baseurl";
import { rejectIfTabSessionStale } from "./tabSession";
import { dedupeAdapter } from "../Redux/Features/Slices/inflightGuard";

// baseUrl relative भी हो सकता है (local .env में "/api/"), इसलिए page origin के against resolve करो
const API_ORIGIN = new URL(baseUrl || "/", window.location.origin).origin;
const CHANNEL_PREFIX = `${API_ORIGIN}/api/v2/`;
const LABEL = "KSDPL-SC-v1";
const CHANNEL_EXPIRED = 440;

const SKIP_HEADERS = new Set([
  "content-type",
  "content-length",
  "x-requested-with",
  "host",
  "cookie",
  "origin",
  "referer",
]);

const encoder = new TextEncoder();
const decoder = new TextDecoder();

// Secure channel के बाहर वाली URLs (जैसे third-party APIs) के लिए axios का default adapter
const fallbackAdapter =
  typeof axios.getAdapter === "function"
    ? axios.getAdapter(axios.defaults.adapter)
    : axios.defaults.adapter;

let channel = null; // { cid, key, offset }
let handshakePromise = null;

// ─────────────────────────────── helpers ───────────────────────────────

const LOWER = "abcdefghijklmnopqrstuvwxyz";
const DIGITS = "0123456789";
const ALNUM = LOWER + DIGITS;
const HEX = "0123456789abcdef";

function randomFrom(chars, length) {
  const bytes = crypto.getRandomValues(new Uint8Array(length));
  let out = "";
  for (let i = 0; i < length; i++) out += chars[bytes[i] % chars.length];
  return out;
}

// हर call के लिए नया random URL नाम (server इसे पढ़ता नहीं, सिर्फ format check करता है)
function randomEndpoint() {
  const style = crypto.getRandomValues(new Uint8Array(1))[0] % 4;
  let name;
  switch (style) {
    case 0:
      name = randomFrom(ALNUM, 14); // a439hfjas93498
      break;
    case 1:
      name = "i-" + randomFrom(DIGITS, 12); // i-212394282934
      break;
    case 2:
      name = randomFrom(HEX, 12); // 3f9a0c1b7e22
      break;
    default:
      name = randomFrom(LOWER, 1) + randomFrom(ALNUM, 11); // k7d02mzq81xa
  }
  return CHANNEL_PREFIX + name;
}

// Simple request = कोई preflight नहीं
const OUTER_HEADERS = { "Content-Type": "text/plain;charset=UTF-8" };

function toBase64(data) {
  const bytes = data instanceof Uint8Array ? data : new Uint8Array(data);
  let binary = "";
  const chunk = 0x8000;
  for (let i = 0; i < bytes.length; i += chunk) {
    binary += String.fromCharCode.apply(null, bytes.subarray(i, i + chunk));
  }
  return btoa(binary);
}

function fromBase64(str) {
  const binary = atob(str || "");
  const out = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) out[i] = binary.charCodeAt(i);
  return out;
}

function concatBytes(...parts) {
  const total = parts.reduce((sum, p) => sum + p.length, 0);
  const out = new Uint8Array(total);
  let offset = 0;
  for (const p of parts) {
    out.set(p, offset);
    offset += p.length;
  }
  return out;
}

// ─────────────────────────────── handshake ───────────────────────────────

async function handshake() {
  const keyPair = await crypto.subtle.generateKey(
    { name: "ECDH", namedCurve: "P-256" },
    false, // private key extract नहीं हो सकती
    ["deriveBits"]
  );
  const clientPub = new Uint8Array(await crypto.subtle.exportKey("raw", keyPair.publicKey));

  const res = await fetch(randomEndpoint(), {
    method: "POST",
    credentials: "omit",
    headers: OUTER_HEADERS,
    body: JSON.stringify({ k: toBase64(clientPub) }),
  });
  if (!res.ok) throw new Error(`Secure channel handshake failed (${res.status})`);

  const json = await res.json();
  const serverPub = fromBase64(json.k);

  const peerKey = await crypto.subtle.importKey(
    "raw",
    serverPub,
    { name: "ECDH", namedCurve: "P-256" },
    false,
    []
  );
  const sharedSecret = new Uint8Array(
    await crypto.subtle.deriveBits({ name: "ECDH", public: peerKey }, keyPair.privateKey, 256)
  );

  // = SHA256(LABEL || sharedSecret || clientPub || serverPub) — server के बराबर
  const material = await crypto.subtle.digest(
    "SHA-256",
    concatBytes(encoder.encode(LABEL), sharedSecret, clientPub, serverPub)
  );
  const key = await crypto.subtle.importKey("raw", material, { name: "AES-GCM" }, false, [
    "encrypt",
    "decrypt",
  ]);

  return { cid: json.c, key, offset: Number(json.t) - Date.now() };
}

async function getChannel() {
  if (channel) return channel;
  if (!handshakePromise) {
    handshakePromise = handshake()
      .then((ch) => {
        channel = ch;
        return ch;
      })
      .finally(() => {
        handshakePromise = null;
      });
  }
  return handshakePromise;
}

export function resetSecureChannel() {
  channel = null;
}

// ─────────────────────────────── request body ───────────────────────────────

function readHeader(headers, name) {
  if (!headers) return undefined;
  const lower = name.toLowerCase();
  for (const key of Object.keys(headers)) {
    if (key.toLowerCase() === lower) return headers[key];
  }
  return undefined;
}

async function encodeBody(data, headerContentType) {
  let contentType = headerContentType || null;
  let body = new Uint8Array(0);

  if (data == null) {
    contentType = null;
  } else if (typeof FormData !== "undefined" && data instanceof FormData) {
    // Browser खुद multipart body + boundary बनाता है
    const r = new Response(data);
    body = new Uint8Array(await r.arrayBuffer());
    contentType = r.headers.get("Content-Type");
  } else if (data instanceof URLSearchParams) {
    body = encoder.encode(data.toString());
    contentType = contentType || "application/x-www-form-urlencoded;charset=UTF-8";
  } else if (typeof Blob !== "undefined" && data instanceof Blob) {
    body = new Uint8Array(await data.arrayBuffer());
    contentType = contentType || data.type || "application/octet-stream";
  } else if (data instanceof ArrayBuffer) {
    body = new Uint8Array(data);
  } else if (ArrayBuffer.isView(data)) {
    body = new Uint8Array(data.buffer, data.byteOffset, data.byteLength);
  } else if (typeof data === "string") {
    body = encoder.encode(data);
    contentType = contentType || "application/json";
  } else {
    body = encoder.encode(JSON.stringify(data));
    contentType = contentType || "application/json";
  }

  return { body, contentType };
}

function buildSignal(config) {
  const controller = new AbortController();
  let timedOut = false;
  let timer = null;

  if (config.signal) {
    if (config.signal.aborted) controller.abort();
    else config.signal.addEventListener("abort", () => controller.abort(), { once: true });
  }
  if (config.timeout > 0) {
    timer = setTimeout(() => {
      timedOut = true;
      controller.abort();
    }, config.timeout);
  }

  return {
    signal: controller.signal,
    isTimedOut: () => timedOut,
    clear: () => timer && clearTimeout(timer),
  };
}

// ─────────────────────────────── axios adapter ───────────────────────────────

export async function secureAdapter(config) {
  const fullUrl = new URL(axios.getUri(config), window.location.origin);

  // हमारी API नहीं है → normal request
  if (fullUrl.origin !== API_ORIGIN) {
    return fallbackAdapter(config);
  }

  const headerObj = AxiosHeaders.from(config.headers).toJSON();
  const forwardHeaders = {};
  for (const [name, value] of Object.entries(headerObj)) {
    if (value == null || value === false || SKIP_HEADERS.has(name.toLowerCase())) continue;
    forwardHeaders[name] = String(value);
  }

  const { body, contentType } = await encodeBody(config.data, readHeader(headerObj, "Content-Type"));
  const method = (config.method || "get").toUpperCase();
  const path = fullUrl.pathname + fullUrl.search;

  const sig = buildSignal(config);

  try {
    for (let attempt = 0; attempt < 2; attempt++) {
      const ch = await getChannel();

      const inner = {
        m: method,
        p: path,
        h: forwardHeaders,
        ct: contentType,
        b: toBase64(body),
        t: Date.now() + ch.offset,
      };

      const iv = crypto.getRandomValues(new Uint8Array(12));
      const cipher = await crypto.subtle.encrypt(
        { name: "AES-GCM", iv, additionalData: encoder.encode(`q|${ch.cid}`) },
        ch.key,
        encoder.encode(JSON.stringify(inner))
      );

      let res;
      try {
        res = await fetch(randomEndpoint(), {
          method: "POST",
          credentials: config.withCredentials === false ? "omit" : "include",
          headers: OUTER_HEADERS,
          body: JSON.stringify({ c: ch.cid, n: toBase64(iv), d: toBase64(cipher) }),
          signal: sig.signal,
        });
      } catch (err) {
        if (sig.isTimedOut()) {
          throw new AxiosError(
            `timeout of ${config.timeout}ms exceeded`,
            AxiosError.ECONNABORTED,
            config
          );
        }
        if (sig.signal.aborted) throw new CanceledError(undefined, undefined, config);
        throw new AxiosError("Network Error", AxiosError.ERR_NETWORK, config);
      }

      if (res.status === CHANNEL_EXPIRED) {
        channel = null; // नया handshake करके दोबारा भेजेंगे
        continue;
      }

      if (!res.ok) {
        const plainResponse = {
          data: null,
          status: res.status,
          statusText: res.statusText,
          headers: AxiosHeaders.from({}),
          config,
          request: {},
        };
        throw new AxiosError(
          `Request failed with status code ${res.status}`,
          res.status >= 500 ? AxiosError.ERR_BAD_RESPONSE : AxiosError.ERR_BAD_REQUEST,
          config,
          {},
          plainResponse
        );
      }

      const outer = await res.json();
      const plain = await crypto.subtle.decrypt(
        { name: "AES-GCM", iv: fromBase64(outer.n), additionalData: encoder.encode(`s|${ch.cid}`) },
        ch.key,
        fromBase64(outer.d)
      );
      const r = JSON.parse(decoder.decode(plain));

      const bytes = fromBase64(r.b);
      const type = r.ct || "application/octet-stream";

      let data;
      if (config.responseType === "blob") data = new Blob([bytes], { type });
      else if (config.responseType === "arraybuffer") data = bytes.buffer;
      else data = decoder.decode(bytes); // axios का transformResponse JSON parse कर देगा

      const response = {
        data,
        status: r.s,
        statusText: "",
        headers: AxiosHeaders.from({
          "content-type": type,
          ...(r.cd ? { "content-disposition": r.cd } : {}),
        }),
        config,
        request: {},
      };

      const isValid = !config.validateStatus || config.validateStatus(r.s);
      if (isValid) return response;

      throw new AxiosError(
        `Request failed with status code ${r.s}`,
        r.s >= 500 ? AxiosError.ERR_BAD_RESPONSE : AxiosError.ERR_BAD_REQUEST,
        config,
        response.request,
        response
      );
    }

    throw new AxiosError("Secure channel unavailable", AxiosError.ERR_NETWORK, config);
  } finally {
    sig.clear();
  }
}

// ─────────────────────────────── api adapter ────────────────────────────────

const isApiRequest = (config) =>
  new URL(axios.getUri(config), window.location.origin).origin === API_ORIGIN;

// Har KSDPL API call (axiosInstance, plainAxios, direct axios.*) isi adapter se
// jaati hai — double/triple click pe same request in-flight ho to dobara nahi
// bhejte (see Redux/Features/Slices/inflightGuard.js), duplicate entries nahi banti.
export const apiAdapter = dedupeAdapter(
  REACT_APP_SECURE_CHANNEL ? secureAdapter : fallbackAdapter,
  isApiRequest
);

// ─────────────────────────────── global axios ───────────────────────────────

// Kai components seedha axios.get/post use karte hain — index.js me ek baar ye
// call karo taaki wo bhi channel se jaayein. baseURL global set NAHI karte: wo
// calls already `${baseUrl}...` pass karti hain, aur relative baseUrl ("/api/")
// pe double prefix ban jaata. withCredentials + X-Requested-With sirf hamari API
// ke liye — third-party (WhatsApp assistant etc.) pe CORS na toote.
export function installGlobalAxios() {
  axios.defaults.adapter = apiAdapter;

  axios.interceptors.request.use((config) => {
    if (isApiRequest(config)) {
      config.withCredentials = true;
      config.headers.set("X-Requested-With", "XMLHttpRequest");
      rejectIfTabSessionStale(config);
    }
    return config;
  });
}
